<template>
  <Teleport defer to="#drawer-content">
    <div v-bind="$attrs" v-if="isActive">
      <component 
        :is="component" 
        v-if="component" 
        v-bind="$props.props" 
        v-on="events" 
        @close="close" 
      />
      <div v-else-if="content" v-html="content" />
      <slot v-else />
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';

/**
 * @displayName DSideDrawer
 */
const props = defineProps({
  props: Object,
  events: Object,
  programmatic: {
    type: Boolean,
    default: false,
  },
  active: {
    type: Boolean,
    default: false,
  },
  component: [Object, Function],
  content: String,
  width: {
    type: String,
    default: '456px',
  },
  canCancel: {
    type: [Array, Boolean],
    default: () => ['escape', 'outside'],
  },
  onCancel: {
    type: Function,
    default: () => {},
  },
});

const emit = defineEmits(['close', 'update:active']);
const isActive = ref(props.active || false);

// Track if there's an active modal when the drawer opens
const modalWasActive = ref(false);

// Watch for props.active changes
watch(() => props.active, (value) => {
  isActive.value = value;
  
  // Toggle the drawer
  if (value) {
    openDrawer();
  } else {
    closeDrawer();
  }
});

// Function to open the drawer
const openDrawer = () => {
  const drawerToggle = document.getElementById('side-drawer-toggle');
  if (drawerToggle) {
    // Check if there's an open modal
    const openModal = document.querySelector('dialog.modal[open]');
    modalWasActive.value = !!openModal;
    
    // If modal is open, add special class to handle z-index better
    if (modalWasActive.value) {
      document.body.classList.add('drawer-with-modal');
    }
    
    // Open the drawer
    drawerToggle.checked = true;
    document.body.classList.add('drawer-open');
    
    // Set drawer width according to props
    const drawerSide = document.querySelector('.drawer-side > div');
    if (drawerSide) {
      drawerSide.style.width = props.width;
    }
  }
};

// Function to close the drawer
const closeDrawer = () => {
  const drawerToggle = document.getElementById('side-drawer-toggle');
  if (drawerToggle) {
    drawerToggle.checked = false;
    document.body.classList.remove('drawer-open');
    document.body.classList.remove('drawer-with-modal');
  }
};

// Handle keyboard events (Escape)
const keyPress = (event) => {
  if (isActive.value && event.key === 'Escape') {
    const cancelOptions = typeof props.canCancel === 'boolean'
      ? props.canCancel ? ['escape', 'outside'] : []
      : props.canCancel;
      
    if (cancelOptions.includes('escape')) {
      // Only handle escape if no modal is open or this drawer was opened on top of a modal
      const openModal = document.querySelector('dialog.modal[open]');
      if (!openModal || modalWasActive.value) {
        props.onCancel('escape');
        close();
      }
    }
  }
};

// Close function
const close = () => {
  isActive.value = false;
  closeDrawer();
  emit('close');
  emit('update:active', false);
  
  // Cleanup programmatic instances
  if (props.programmatic) {
    nextTick(() => {
      // Find container element and remove it after animation
      setTimeout(() => {
        const container = document.getElementById('drawer-container');
        if (container && container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }, 300); // Match transition duration
    });
  }
};

// Cancel method (for outside clicks, etc.)
const cancel = (method) => {
  const cancelOptions = typeof props.canCancel === 'boolean'
    ? props.canCancel ? ['escape', 'outside'] : []
    : props.canCancel;
    
  if (!cancelOptions.includes(method)) return;
  props.onCancel(method);
  close();
};

// Add event listener for escape key
onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', keyPress);
    
    // Initialize if programmatic
    if (props.programmatic) {
      isActive.value = true;
      nextTick(() => {
        openDrawer();
      });
    }
  }
});

// Remove event listener on unmount
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('keydown', keyPress);
  }
});

defineExpose({
  isActive,
  close,
  cancel
});
</script>


<style scoped>
:deep(:is(.input, .select, .dropdown)) {
  width: 100%;
}
</style>